import { Dispatch, SetStateAction, useCallback, useContext } from 'react';
import type { TSelectItemDialogState } from '@modules/items';
import { AccessType } from '@modules/access/types';
import { EFolderType, isTFolder } from '@modules/items/types/folders.types';
import { useCreateInlineBlock, useSaveInlineBlock } from '@modules/inlineBlocks';
import type { TSingleFieldDialogState } from '@shared/components';
import { ToastContext } from '@shared/contexts';
import { TERMINOLOGY } from '@shared/i18n';
import type { TMap } from '@shared/types';
import { buildHref } from '@shared/utils';
import DocumentContext from '../contexts/DocumentContext.context';
import { useCreate as useCreateDoc, useUpdateBlockContent } from '../api';
import captureSelectionSnapshot from '../helpers/captureSelectionSnapshot.helper';
import getItemPathByItemType from '../helpers/getItemPathByItemType.helper';
import { TBlockItemType } from '../types';

type TParams = {
  setSelectBlockTargetDialogState: Dispatch<SetStateAction<TSelectItemDialogState | undefined>>;
  setSingleFieldDialogState: Dispatch<SetStateAction<TSingleFieldDialogState<'input'> | undefined>>;
};

const openInNewTab = (kind: TBlockItemType, id: string, params?: TMap<string, string>) =>
  window.open(
    buildHref(`${window.location.origin}/${getItemPathByItemType(kind)}/${id}`, params),
    '_blank',
    'noopener,noreferrer'
  );

const useCreateBlock = ({ setSelectBlockTargetDialogState, setSingleFieldDialogState }: TParams) => {
  const { doc, editor } = useContext(DocumentContext);
  const { show } = useContext(ToastContext);
  const createBlock = useCreateDoc();
  const createInlineBlock = useCreateInlineBlock();
  const saveInlineBlock = useSaveInlineBlock();
  const updateBlockContent = useUpdateBlockContent();

  const handleCreateBlock = useCallback(() => {
    const snapshot = captureSelectionSnapshot(editor);
    if (!snapshot) return;
    const { kind } = snapshot;
    const label = kind === 'INLINE_BLOCK' ? TERMINOLOGY.inlineBlock : TERMINOLOGY.block;

    const create = (parent: string) => {
      const singleFieldDialogState: TSingleFieldDialogState<'input'> = {
        isOpen: true,
        defaultText: '',
        title: `New ${label.lower}`,
        label: 'Name',
        buttonLabel: 'Create',
        onSubmit: async (data) => {
          const onSuccess = ({ _id }: { _id: string }) => {
            openInNewTab(kind, _id);
            setSingleFieldDialogState({ ...singleFieldDialogState, isOpen: false });
            editor.commands.focus();
          };

          if (kind === 'INLINE_BLOCK') {
            await createInlineBlock.mutateAsync({ parent, name: data.text, initValue: snapshot.text }, { onSuccess });
          } else {
            await createBlock.mutateAsync(
              { parent, name: data.text, initContent: { originId: doc._id, html: snapshot.html } },
              { onSuccess }
            );
          }
        },
      };
      setSingleFieldDialogState(singleFieldDialogState);
    };

    setSelectBlockTargetDialogState({
      isOpen: true,
      onSelect: async ([target]) => {
        if (kind === 'INLINE_BLOCK') {
          await saveInlineBlock.mutateAsync({ id: target._id, value: snapshot.text });
          show('success', `${TERMINOLOGY.inlineBlock.upper} updated.`);
        } else {
          await updateBlockContent.mutateAsync({ id: target._id, originId: doc._id, html: snapshot.html });
        }
        setSelectBlockTargetDialogState(undefined);
        openInNewTab(kind, target._id, { view: 'draft' });
        editor.commands.focus();
      },
      continueAction: {
        text: 'Create',
        getDisabledReason: (data) => {
          if (!isTFolder(data) || data.folderType !== EFolderType.BLOCK)
            return `Open a folder of the ${TERMINOLOGY.productDatabase.lower} or ${TERMINOLOGY.qmDatabase.lower} to create one there.`;
          if (data.accessRight < AccessType.EDIT)
            return `You don't have edit rights for this ${TERMINOLOGY.folder.lower}.`;
          return undefined;
        },
        onContinue: (folder) => {
          setSelectBlockTargetDialogState(undefined);
          create(folder._id);
        },
      },
    });
  }, [doc._id]);

  return handleCreateBlock;
};

export default useCreateBlock;
